import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider, useTheme } from "@/lib/theme";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import PitchViewer from "@/pages/PitchViewer";
import Register from "@/pages/Register";
import DashboardLayout from "@/pages/dashboard/Layout";
import Dashboard from "@/pages/dashboard/Dashboard";
import HomePage from "@/pages/dashboard/HomePage";
import BriefingPage from "@/pages/dashboard/Briefing";
import Orchestration from "@/pages/dashboard/Orchestration";
import ChatRedirect from "@/components/chat/ChatRedirect";
import Matches from "@/pages/dashboard/Matches";
import Campaigns from "@/pages/dashboard/Campaigns";
import Credits from "@/pages/dashboard/Credits";
import AuditLog from "@/pages/dashboard/AuditLog";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";
import Integrations from "@/pages/dashboard/Integrations";
import Branding from "@/pages/dashboard/Branding";
import PlatformAdmin from "@/pages/dashboard/PlatformAdmin";
import Bias from "@/pages/dashboard/Bias";
import Voice from "@/pages/dashboard/Voice";
import Compliance from "@/pages/dashboard/Compliance";
import ToneOfVoice from "@/pages/dashboard/ToneOfVoice";
import AgentMemoryDashboard from "@/pages/dashboard/AgentMemory";
import Team from "@/pages/dashboard/Team";
import Feedback from "@/pages/dashboard/Feedback";
import WorkspaceSettings from "@/pages/dashboard/WorkspaceSettings";
import AcceptInvite from "@/pages/AcceptInvite";
import OnboardingWizard from "@/pages/OnboardingWizard";
import ScrollToTop from "@/components/ScrollToTop";

import SalesSignals from "@/pages/dashboard/sales/Signals";
import JobChanges from "@/pages/dashboard/sales/JobChanges";
import SalesCompanies from "@/pages/dashboard/sales/Companies";
import SalesContacts from "@/pages/dashboard/sales/Contacts";
import SalesOutreach from "@/pages/dashboard/sales/Outreach";
import SalesPipeline from "@/pages/dashboard/sales/Pipeline";
import TalentSearch from "@/pages/dashboard/recruit/Search";
import Shortlists from "@/pages/dashboard/recruit/Shortlists";
import TalentPool from "@/pages/dashboard/recruit/TalentPool";
import CandidateOutreach from "@/pages/dashboard/recruit/Outreach";
import CandidatePipeline from "@/pages/dashboard/recruit/Pipeline";
import CandidatePage from "@/pages/dashboard/recruit/CandidatePage";
import StaffingCandidates from "@/pages/dashboard/staffing/Candidates";
import StaffingJobs from "@/pages/dashboard/staffing/Jobs";
import StaffingPipeline from "@/pages/dashboard/staffing/Pipeline";
import StaffingDeals from "@/pages/dashboard/staffing/Deals";
import StaffingOutreach from "@/pages/dashboard/staffing/Outreach";
import Network from "@/pages/dashboard/Network";
import MyFeedback from "@/pages/dashboard/MyFeedback";

import "@/App.css";

function Protected({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-[var(--jr-text-2)]">
                <div className="font-mono text-sm">Verbinden met Orchestrator…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function OnboardingGate({ children }) {
    const { workspace } = useAuth();
    const [dismissed, setDismissed] = useState(false);
    useEffect(() => {
        const open = () => setDismissed(false);
        window.addEventListener("jr:open-onboarding", open);
        return () => window.removeEventListener("jr:open-onboarding", open);
    }, []);
    const needsOnboarding = workspace && !workspace.onboarding_completed && !dismissed;
    return (
        <>
            {children}
            {needsOnboarding && <OnboardingWizard onClose={() => setDismissed(true)} />}
        </>
    );
}

function ToastBar() {
    const { theme } = useTheme();
    return <Toaster position="bottom-center" theme={theme} richColors closeButton />;
}

export default function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <ScrollToTop />
                    <div className="app-root bg-grain">
                        <ToastBar />
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/accept-invite" element={<AcceptInvite />} />
                        <Route path="/p/:token" element={<PitchViewer />} />
                        <Route path="/app" element={<Protected><OnboardingGate><DashboardLayout /></OnboardingGate></Protected>}>
                            <Route index element={<Navigate to="home" replace />} />
                            <Route path="home" element={<HomePage />} />
                            <Route path="dashboard" element={<Dashboard />} />
                            <Route path="briefing" element={<BriefingPage />} />
                            <Route path="orchestration" element={<Navigate to="/app/agents" replace />} />
                            {/* Master Chat is now the floating widget — legacy links open it via redirect */}
                            <Route path="chat" element={<ChatRedirect />} />
                            <Route path="chat/:masterId" element={<ChatRedirect />} />
                            <Route path="matches" element={<Matches />} />
                            {/* Sales pillar */}
                            <Route path="sales/signals" element={<SalesSignals />} />
                            <Route path="sales/job-changes" element={<JobChanges />} />
                            <Route path="sales/companies" element={<SalesCompanies />} />
                            <Route path="sales/contacts" element={<SalesContacts />} />
                            <Route path="sales/outreach" element={<SalesOutreach />} />
                            <Route path="sales/pipeline" element={<SalesPipeline />} />
                            {/* Recruitment pillar */}
                            <Route path="recruit/search" element={<TalentSearch />} />
                            <Route path="recruit/pool" element={<TalentPool />} />
                            <Route path="recruit/shortlists" element={<Shortlists />} />
                            <Route path="recruit/outreach" element={<CandidateOutreach />} />
                            <Route path="recruit/pipeline" element={<CandidatePipeline />} />
                            <Route path="recruit/candidate/:id" element={<CandidatePage />} />
                            <Route path="staffing/candidates" element={<StaffingCandidates />} />
                            <Route path="staffing/jobs" element={<StaffingJobs />} />
                            <Route path="staffing/pipeline" element={<StaffingPipeline />} />
                            <Route path="staffing/deals" element={<StaffingDeals />} />
                            <Route path="staffing/outreach" element={<StaffingOutreach />} />
                            <Route path="network" element={<Network />} />
                            <Route path="feedback/mine" element={<MyFeedback />} />
                            {/* Legacy redirects */}
                            <Route path="leads" element={<Navigate to="/app/sales/companies" replace />} />
                            <Route path="candidates" element={<Navigate to="/app/recruit/search" replace />} />
                            <Route path="campaigns" element={<Campaigns />} />
                            <Route path="sales/campaigns" element={<Campaigns defaultType="sales" />} />
                            <Route path="recruit/campaigns" element={<Campaigns defaultType="recruitment" />} />
                            {/* Settings */}
                            <Route path="tone-of-voice" element={<ToneOfVoice />} />
                            <Route path="agent-memory" element={<AgentMemoryDashboard />} />
                            <Route path="team" element={<Team />} />
                            <Route path="feedback" element={<Feedback />} />
                            <Route path="workspace" element={<WorkspaceSettings />} />
                            <Route path="agents" element={<Orchestration />} />
                            <Route path="integrations" element={<Integrations />} />
                            <Route path="branding" element={<Branding />} />
                            <Route path="platform-admin" element={<PlatformAdmin />} />
                            <Route path="bias" element={<Bias />} />
                            <Route path="voice" element={<Voice />} />
                            <Route path="compliance" element={<Compliance />} />
                            <Route path="credits" element={<Credits />} />
                            <Route path="audit" element={<AuditLog />} />
                        </Route>
                        <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
                        <Route path="/billing/cancel" element={<Protected><BillingCancel /></Protected>} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </div>
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}
